/* global JSX */
import { Alert, Box, Chip, Stack, Typography } from "@mui/material";
import * as React from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { IModifyUser, ModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { AppUtils } from "../../../framework/appUtils";
import { IUserListItem } from "../../../models/user/userListItem";
import { UserListItems } from "../../../models/user/userListItems";
import { ToolTitle } from "../../framework/Tool/ToolTitle";
import { SelectUserDialog } from "../SelectUserDialog/SelectUserDialog";
import { ListStateIconSuccess } from "source/components/framework/List/ListStateIconSuccess";
import { ApproveButton } from "source/components/framework/Button/ApproveButton";
import { AddUserToolButton } from "source/components/framework/Button/AddUserToolButton";
import { RemoveUserToolButton } from "source/components/framework/Button/RemoveUserToolButton";

//UserBlockItem
export interface IUserBlockItemProp {
    avatarOnly?: boolean;
    user: IModifyUser;
    userCode?: string;
    selectedCodes: string[];
    onClick: (code: string) => void;
    onApprove?: (code: string) => void;
}

export class UserBlockItem extends React.Component<IUserBlockItemProp, {}> {
    handleApproveClick = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.onApprove(this.props.user.code);
        return 0;
    };

    handleBadgeClick = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.onClick(this.props.user.code);
        return 0;
    };

    render() {
        const props = this.props;
        const user = props.user;
        const selected = props.selectedCodes.indexOf(user.code) > -1;

        if (props.avatarOnly) {
            return (
                <AvatarImage
                    id={user.code}
                    marginLeft={"4px"}
                    name={user.name}
                    size={30}
                    image={user.picture}
                    tooltip={user.name}
                    onClick={() => props.onClick(user.code)}
                />
            );
        }

        return (
            <Chip
                label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography sx={{ overflow: "hidden", flexShrink: 5 }}>{user.name}</Typography>
                        {(!!props.onApprove && !!props.userCode && !user.date && props.userCode === user.code) &&
                        (
                            <ApproveButton
                                onClick={this.handleApproveClick}
                                sx={{ marginLeft: ".5rem", padding: "0 9px" }}
                            />
                        )}
                        {(!!props.onApprove && !!user.date) &&
                            <>
                                <ListStateIconSuccess color="success" />
                                <Typography variant="caption" sx={{ color: "text.secondary", marginLeft: ".25rem" }} title={Translations.Approved + ": " + Base.timeToDateTimeStr(user.date)}>{Base.timeToDateTimeStr(user.date)}</Typography>
                            </>
                        }
                    </Box>
                }
                avatar={
                    <AvatarImage
                        id={user.code}
                        marginLeft={"4px"}
                        name={user.name}
                        image={user.picture}
                        tooltip={user.name}
                    />
                }
                variant={selected ? "filled" : "outlined"}
                disabled={false}
                onClick={(e) => {
                    e.stopPropagation();
                    props.onClick(user.code);
                }}
            />
        );
    }
}

//UserBlocks
export interface IUserBlocksProp {
    avatarOnly?: boolean;
    companyId: string;
    userCode?: string;
    isReadOnly?: boolean;
    title: string | JSX.Element;
    noUsersMessage?: string;
    noUsersMessageUseInReadOnly?: boolean;
    parentIsNew?: boolean;
    parentIsNewMessage?: string;
    fixedUsers?: IModifyUser[];
    fixedIgnoreUserCodes?: string[];
    users: IModifyUser[];
    removeUsersConfirmation?: string;
    margin?: string | number;
    maxHeight?: string | number;
    initiallySelectedIds?: string[];
    onAddUsers: (users: IModifyUser[]) => Promise<ApiSuccess>;
    onRemoveUsers: (userCodes: string[]) => Promise<ApiSuccess>;
    onGetUserSelectListItems?: (companyId: string, pageSize: number, page: number, filter: string,
        ignoreUserIds: string[], ignoreUserCodes: string[], activityState: number, ignoreRecordUserConnected: boolean, ignoreQrCodeUsers: boolean, requireQrCodeUsers: boolean,
        doNotIgnoreRecordUserConnectedUserCode: string, sortColumn: string, sortOrderIsAsc: boolean) => Promise<UserListItems>;
    onApprove?: (userCode: string) => void;
}

interface IUserBlocksState {
    selectedFixedCode: string;
    selectedCode: string;
    showSelectUserDialog: boolean;
}

export class UserBlocks extends React.Component<IUserBlocksProp, IUserBlocksState> {
    static defaultProps = {
        margin: "1rem 0 0 0"
    };

    constructor(props) {
        super(props);
        this.state = {
            selectedFixedCode: "",
            selectedCode: "",
            showSelectUserDialog: false
        };
    }

    componentDidUpdate(prevProps: IUserBlocksProp, prevState: IUserBlocksState) {
        const props = this.props;
        const state = this.state;
        const prevHash = JSON.stringify(prevProps.users);
        const hash = JSON.stringify(props.users);
        if (prevHash === hash) return;
        const userCodes = props.users.map(i => i.code);
        this.setState({
            selectedFixedCode: userCodes.indexOf(state.selectedFixedCode) > -1 ? state.selectedFixedCode : "",
            selectedCode: userCodes.indexOf(state.selectedCode) > -1 ? state.selectedCode : ""
        });
    }

    handleFixedBlockClick = (selectedFixedCode: string) => {
        this.setState({
            selectedFixedCode,
            selectedCode: ""
        });
    };

    handleBlockClick = (selectedCode: string) => {
        this.setState({
            selectedFixedCode: "",
            selectedCode
        });
    };

    // #region Add User
    handleAdd = () => {
        this.setState({
            showSelectUserDialog: true
        });
    };

    onSelectUserDialogOk = async (users: IUserListItem[]) => {
        const props = this.props;
        if (!users || users.length < 1) return;
        const newUsers = users.map(user => {
            const newUser = new ModifyUser();
            newUser.code = user.code;
            newUser.name = user.getName();
            newUser.picture = user.picture;
            newUser.date = Base.getNowTicks();
            return newUser;
        });
        // Call server
        const result = await AppUtils.callService(() => props.onAddUsers(newUsers));
        if (!result) return;
        this.setState({
            showSelectUserDialog: false
        });
    };

    onSelectUserDialogCancel = () => {
        this.setState({
            showSelectUserDialog: false
        });
    };
    // #endregion Add User

    // #region Remove User
    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (!state.selectedCode) return;
        if (await AppUtils.showConfirmationDialog(props.removeUsersConfirmation ?? Translations.DoYouReallyWantToRemoveSelectedUsers, false) === ConfirmationDialogResult.Yes) {
            // Call server
            await AppUtils.callService(() => props.onRemoveUsers([state.selectedCode]));
        }
    };
    // #endregion Remove User

    render() {
        const props = this.props;
        const state = this.state;
        const userCodes = props.users.map(i => i.code);
        const fixedUsers = props.fixedUsers ? props.fixedUsers.filter(i => !userCodes.includes(i.code)) : [];
        const users = props.users;
        const titleIsString = Base.isString(props.title);
        return (
            <>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ margin: props.margin }}>
                    {titleIsString &&
                        <ToolTitle
                            title={props.title}
                        />
                    }
                    {!titleIsString &&
                        props.title
                    }
                    <Stack direction="row">
                        {!props.isReadOnly &&
                            <AddUserToolButton
                                disabled={props.parentIsNew}
                                tooltip={Translations.AddUser}
                                onClick={this.handleAdd}
                            />
                        }
                        {!props.isReadOnly &&
                            <RemoveUserToolButton
                                tooltip={Translations.RemoveUser}
                                disabled={!state.selectedCode}
                                onClick={this.handleRemove}
                            />
                        }
                    </Stack>
                </Box>
                <Stack direction="row" spacing={1} sx={{ marginTop: ".5rem", maxHeight: props.maxHeight ? `calc(${props.maxHeight} - 48px)` : null, overflowY: "auto" }} useFlexGap flexWrap="wrap">
                    {!props.isReadOnly && !!props.parentIsNew && !!props.parentIsNewMessage &&
                        <Alert severity="info" sx={{ width: "100%" }}>{props.parentIsNewMessage}</Alert>
                    }
                    {(!props.isReadOnly || props.noUsersMessageUseInReadOnly) && users.length < 1 && !!props.noUsersMessage &&
                        <Alert severity="info" sx={{ width: "100%" }}>{props.noUsersMessage}</Alert>
                    }
                    {fixedUsers.map((user) =>
                        <UserBlockItem
                            key={user.code}
                            avatarOnly={props.avatarOnly}
                            userCode={props.userCode}
                            user={user}
                            selectedCodes={[state.selectedFixedCode]}
                            onClick={this.handleFixedBlockClick}
                            onApprove={props.onApprove}
                        />
                    )}
                    {users.map((user) =>
                        <UserBlockItem
                            key={user.code}
                            avatarOnly={props.avatarOnly}
                            userCode={props.userCode}
                            user={user}
                            selectedCodes={[state.selectedCode]}
                            onClick={this.handleBlockClick}
                            onApprove={props.onApprove}
                        />
                    )}
                </Stack>
                {state.showSelectUserDialog &&
                    <SelectUserDialog
                        companyId={props.companyId}
                        ignoreUserCodes={Base.getUniqueStringItems((fixedUsers.concat(users)).map(i => i.code).concat(props.fixedIgnoreUserCodes || []))}
                        initiallySelectedIds={props.initiallySelectedIds}
                        multiSelect={true}
                        onOk={this.onSelectUserDialogOk}
                        onCancel={this.onSelectUserDialogCancel}
                        onGetUserSelectListItems={props.onGetUserSelectListItems}
                    />
                }
            </>
        );
    }
}